import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faPlus } from "@fortawesome/free-solid-svg-icons";
import { crmPostventaV2Api } from "./crmPostventaV2Api";
import { getSectionMeta } from "./processCatalog";
import ProcessActionModal, { buildAttemptLabel, formatDateLabel, formatTimeLabel, resolvePriorityTone } from "./components/ProcessActionModal";
import "./gestion-unificada.css";

const makeToday = () => new Date().toISOString().slice(0, 10);

const PRIORITY_LABELS = {
  low: "Baja",
  medium: "Media",
  high: "Alta",
  critical: "Crítica",
};

function PriorityChip({ value }) {
  if (!value) {
    return <span className="crm-gestion-unificada__prioridad is-neutral">Sin prioridad</span>;
  }
  const tone = resolvePriorityTone(value);
  return (
    <span className={`crm-gestion-unificada__prioridad is-${tone === "low" ? "baja" : tone === "medium" ? "media" : tone === "high" ? "alta" : "critica"}`}>
      {PRIORITY_LABELS[tone] || value}
    </span>
  );
}

function GestionRow({ item, onManage, disabled }) {
  const nextDate = formatDateLabel(item?.fechaProximaAccion);
  const nextTime = formatTimeLabel(item?.fechaProximaAccion);
  const attemptLabel = buildAttemptLabel(item);
  const readOnly = item?.esSoloLectura || item?.puedeGestionar === false;

  return (
    <div className="crm-gestion-unificada__row">
      <div className="crm-gestion-unificada__comercio">
        <strong>{item?.comercio || "Sin nombre"}</strong>
        <span>
          {item?.cuit ? <><b>CUIT:</b> {item.cuit} &nbsp;&nbsp;</> : null}
          {item?.numeroTerminal ? <><b>Terminal:</b> {item.numeroTerminal} &nbsp;&nbsp;</> : null}
          {item?.numeroComercio ? <><b>Comercio:</b> {item.numeroComercio}</> : null}
        </span>
      </div>

      <div>
        <span className="crm-gestion-unificada__chip">{item?.estadoLabel || item?.estadoActual || "-"}</span>
        <div className="crm-gestion-unificada__seguimiento-meta">
          {item?.canalPermitido ? <div><b>Canal:</b> {item.canalPermitido}</div> : null}
          {attemptLabel ? <div>{attemptLabel}</div> : null}
        </div>
      </div>

      <div className="crm-gestion-unificada__accion">
        {item?.accionPermitida ? <strong>{item.accionPermitida}</strong> : <span>Sin próxima acción</span>}
        {nextDate ? <span><b>Fecha:</b> {nextDate}</span> : null}
        {nextTime ? <span><b>Horario:</b> {nextTime}</span> : null}
      </div>

      <div className="crm-gestion-unificada__prioridad-wrap">
        <PriorityChip value={item?.prioridad} />
      </div>

      <div>
        <span className={`crm-gestion-unificada__responsable ${item?.responsable ? "" : "is-unassigned"}`}>
          {item?.responsable || "Sin asignar"}
        </span>
      </div>

      <div>
        <button
          type="button"
          className={`crm-gestion-unificada__manage-btn ${readOnly ? "is-readonly" : ""}`}
          onClick={() => onManage(item)}
          disabled={disabled}
        >
          {readOnly ? "Ver detalle" : "Gestionar"}
        </button>
      </div>
    </div>
  );
}

export default function GestionProcesoPanel({ slug }) {
  const section = getSectionMeta(slug);
  const [fecha, setFecha] = useState(makeToday());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [existingEvidences, setExistingEvidences] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const loadRows = async () => {
    setLoading(true);
    try {
      const response = await crmPostventaV2Api.obtenerCola(slug, { fecha });
      setRows((response?.filas || []).map((item) => ({ ...item, procesoSlug: slug, procesoLabel: section.shortTitle })));
    } catch (error) {
      Swal.fire(section.shortTitle, error.message || "No pudimos cargar las gestiones.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, fecha]);

  const openDetail = async (item) => {
    try {
      setSubmitting(true);
      const processSlug = item.procesoSlug || slug;
      const [detailResponse, evidencesResponse] = await Promise.all([
        crmPostventaV2Api.obtenerDetalle(processSlug, item.id),
        crmPostventaV2Api.obtenerEvidencias(item.id),
      ]);
      setDetail({
        ...detailResponse,
        instancia: {
          ...detailResponse?.instancia,
          procesoCodigo: detailResponse?.instancia?.procesoCodigo || processSlug,
          procesoSlug: processSlug,
        },
      });
      setExistingEvidences(evidencesResponse || []);
      setModalOpen(true);
    } catch (error) {
      Swal.fire(section.shortTitle, error.message || "No pudimos abrir la gestión.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerar = async () => {
    try {
      setSubmitting(true);
      const action = section.supportsReconcile ? crmPostventaV2Api.reconciliar : crmPostventaV2Api.generar;
      const preview = await action(slug, { dryRun: true, fecha });

      if (!preview?.permiteEjecucionReal) {
        await Swal.fire({
          title: "Ejecución bloqueada",
          text: preview?.message || "No hay elementos para generar en este momento.",
          icon: "warning",
        });
        return;
      }

      const confirmation = await Swal.fire({
        title: "Confirmar generación",
        text: `Se crearán ${preview?.crear ?? 0} procesos, limitados a ${preview?.maximumItemsPerRun ?? 0} por ejecución.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: section.actionLabel || "Generar",
        cancelButtonText: "Cancelar",
      });

      if (!confirmation.isConfirmed) {
        return;
      }

      const response = await action(slug, { dryRun: false, fecha });
      await Swal.fire("Operación completada", response?.message || "Procesos generados correctamente.", "success");
      await loadRows();
    } catch (error) {
      Swal.fire(section.shortTitle, error.message || "No pudimos generar los procesos.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAction = async (payload) => {
    if (!detail?.instancia?.id) return;

    try {
      setSubmitting(true);
      const response = await crmPostventaV2Api.ejecutarAccion(detail.instancia.procesoSlug || slug, detail.instancia.id, payload);
      await Swal.fire(section.shortTitle, response?.message || "Gestión registrada.", "success");
      setModalOpen(false);
      setDetail(null);
      setExistingEvidences([]);
      await loadRows();
    } catch (error) {
      if (error?.status === 409) {
        Swal.fire(section.shortTitle, "La gestión fue modificada por otro usuario. Actualizá e intentá de nuevo.", "warning");
      } else {
        Swal.fire(section.shortTitle, error.message || "No pudimos guardar la gestión.", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canGenerate = !section.isSpecial && (section.supportsGenerate || section.supportsReconcile);

  return (
    <div className="crm-gestion-unificada">
      <div className="crm-gestion-unificada__top-panel">
        <div className="crm-gestion-unificada__top-copy">
          <h3>{section.shortTitle}</h3>
          <p>{section.description}</p>
        </div>

        <div className="crm-gestion-unificada__top-actions">
          {!section.isSpecial ? (
            <label className="crm-gestion-unificada__date">
              <span className="visually-hidden">Fecha de referencia</span>
              <input type="date" value={fecha} onChange={(event) => setFecha(event.target.value)} aria-label="Fecha de referencia" />
            </label>
          ) : null}

          {canGenerate ? (
            <button type="button" className="crm-gestion-unificada__generar" onClick={handleGenerar} disabled={loading || submitting}>
              <FontAwesomeIcon icon={faPlus} /> Generar
            </button>
          ) : null}

          <button
            type="button"
            className="crm-gestion-unificada__refresh"
            onClick={loadRows}
            disabled={loading || submitting}
            aria-label="Actualizar"
          >
            <FontAwesomeIcon icon={faArrowsRotate} spin={loading} />
          </button>
        </div>
      </div>

      <div className="crm-gestion-unificada__columns" role="row">
        <span>Comercio</span>
        <span>Seguimiento</span>
        <span>Próx. acción</span>
        <span>Prioridad</span>
        <span>Responsable</span>
        <span>Acciones</span>
      </div>

      {loading ? (
        <div className="crm-gestion-unificada__loading">
          <Spinner animation="border" size="sm" className="me-2" /> Cargando gestiones...
        </div>
      ) : rows.length ? (
        <div className="crm-gestion-unificada__list">
          {rows.map((item) => (
            <GestionRow key={`${item.procesoCodigo || slug}-${item.id}`} item={item} onManage={openDetail} disabled={submitting} />
          ))}
        </div>
      ) : (
        <div className="crm-gestion-unificada__empty">No hay gestiones pendientes para esta categoría.</div>
      )}

      <ProcessActionModal
        show={modalOpen}
        onHide={() => setModalOpen(false)}
        detail={detail}
        existingEvidences={existingEvidences}
        submitting={submitting}
        onSubmit={handleSubmitAction}
      />
    </div>
  );
}
